import React, { useState, useEffect } from "react";
import { BookOpen, Calendar, CheckCircle, XCircle, Clock } from "lucide-react";
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import config from "../../config";

const UserBookSuggestionsSection = ({ userId }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userId) {
      fetchUserSuggestions();
    }
  }, [userId]);

  const fetchUserSuggestions = async () => {
    try {
      const response = await fetch(
        `${config.url}/api/admin/suggestions/user/${userId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = await response.json();

      if (data.responseCode === 200 && data.responseData) {
        setSuggestions(data.responseData);
      } else {
        console.error("Failed to fetch suggestions:", data.message);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      toast.error("Failed to load book suggestions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "APPROVED":
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case "REJECTED":
        return <XCircle className="w-4 h-4 text-red-600" />;
      case "PENDING":
      default:
        return <Clock className="w-4 h-4 text-yellow-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      APPROVED: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      REJECTED: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
      PENDING: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
    };

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
          styles[status] || styles.PENDING
        }`}
      >
        {getStatusIcon(status)}
        {status}
      </span>
    );
  };

  const formatDate = (dateArray) => {
    if (!dateArray || dateArray.length < 3) return "N/A";
    // dateArray format: [year, month, day, hour, minute, second]
    const [year, month, day] = dateArray;
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-3xl p-6"
      >
        <h3 className="text-xl font-semibold flex items-center gap-3 dark:text-white mb-4">
          <BookOpen className="w-6 h-6 text-primary" />
          Book Suggestions
        </h3>
        <p className="text-gray-500 text-center py-8">Loading...</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-2xl shadow-3xl p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-semibold flex items-center gap-3 dark:text-white">
          <BookOpen className="w-6 h-6 text-primary" />
          Book Suggestions
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {suggestions.length} total suggestions
        </span>
      </div>

      {suggestions.length === 0 ? (
        <div className="text-center py-8">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">
            This user hasn't suggested any books yet.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion, index) => (
            <motion.div
              key={suggestion.sugId}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 hover:shadow-md hover:border-primary/30 dark:hover:border-primary/30 transition-all"
            >
              <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="font-semibold text-gray-900 dark:text-white text-lg">
                      {suggestion.title}
                    </h4>
                    <div className="flex-shrink-0">
                      {getStatusBadge(suggestion.status)}
                    </div>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">
                    by <span className="font-medium">{suggestion.author}</span>
                  </p>
                  
                  <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-gray-400 mb-3">
                    {suggestion.edition && (
                      <span className="flex items-center gap-1">
                        <span className="font-medium">Edition:</span> {suggestion.edition}
                      </span>
                    )}
                    {suggestion.bookLanguage && (
                      <span className="flex items-center gap-1">
                        <span className="font-medium">Language:</span> {suggestion.bookLanguage}
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(suggestion.creationDate)}
                    </span>
                  </div>
                  
                  {suggestion.message && (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border-l-4 border-primary">
                      <p className="text-sm text-gray-700 dark:text-gray-300 italic">
                        "{suggestion.message}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default UserBookSuggestionsSection;
